function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React from 'react';
import cn from 'clsx';
import { bool, string } from 'prop-types';
import List from '../List/index.jsx';
import IconStack from '../IconStack/index.jsx';
import Link from '../../atoms/Link/index.jsx';
import capitalize from '../../utils/capitalize.js';

/**
 * Get the social media URLs for each platform.
 */
const getSocialMediaUrls = ({
  rssFeed,
  twitterHandle,
  facebookProfile,
  pinterestProfile,
  youtubeChannel,
  instagramProfile,
  tiktokProfile
} = {}) => {
  const platforms = {
    facebook: facebookProfile ? `https://www.facebook.com/${facebookProfile}` : null,
    twitter: twitterHandle ? `https://twitter.com/${twitterHandle}` : null,
    instagram: instagramProfile ? `https://www.instagram.com/${instagramProfile}` : null,
    pinterest: pinterestProfile ? `http://uk.pinterest.com/${pinterestProfile}` : null,
    youtube: youtubeChannel ? `https://www.youtube.com/channel/${youtubeChannel}` : null,
    tiktok: tiktokProfile ? `https://www.tiktok.com/${tiktokProfile}` : null,
    rss: rssFeed || null
  };
  return Object.keys(platforms).reduce((accumalator, name) => {
    if (platforms[name]) {
      accumalator[name] = platforms[name];
    }
    return accumalator;
  }, {});
};

/**
 * Get the icons for a platform.
 */
const getIcons = (platform, coloured) => {
  const baseSize = 25;
  if (coloured) {
    return [{
      name: platform,
      color: 'baseWhite',
      size: 22
    }, {
      name: 'circle',
      size: 30,
      color: `social${capitalize(platform)}`
    }];
  }
  if (platform === 'pinterest') {
    return [{
      name: 'pinterest-complete',
      color: 'inherit',
      size: baseSize
    }];
  }
  return [{
    name: platform,
    color: 'inherit',
    size: baseSize
  }];
};

/**
 * SocialFollow component.
 */
const SocialFollow = ({
  className,
  rssFeed,
  twitterHandle,
  facebookProfile,
  pinterestProfile,
  youtubeChannel,
  instagramProfile,
  tiktokProfile,
  coloured
}) => {
  const socialMediaUrls = getSocialMediaUrls({
    rssFeed,
    twitterHandle,
    facebookProfile,
    pinterestProfile,
    youtubeChannel,
    instagramProfile,
    tiktokProfile
  });
  return /*#__PURE__*/React.createElement(List, {
    horizontal: true,
    className: cn('social-follow', className)
  }, Object.keys(socialMediaUrls).map(platform => /*#__PURE__*/React.createElement(List.Item, {
    key: platform,
    className: `social-follow__${platform}`
  }, /*#__PURE__*/React.createElement(Link, {
    href: socialMediaUrls[platform],
    className: 'social-follow__link' + (coloured ? " pa-xxs" : ""),
    target: "_blank",
    rel: "nofollow noopener noreferrer"
  }, /*#__PURE__*/React.createElement(IconStack, {
    className: "social-follow__icon",
    srOnly: `Visit us on ${capitalize(platform)}`
  }, getIcons(platform, coloured).map(icon => /*#__PURE__*/React.createElement(IconStack.Icon, _extends({
    key: `${platform}-${icon.name}`
  }, icon))))))));
};
SocialFollow.defaultProps = {
  className: null,
  rssFeed: null,
  twitterHandle: null,
  facebookProfile: null,
  pinterestProfile: null,
  youtubeChannel: null,
  instagramProfile: null,
  tiktokProfile: null,
  coloured: false
};
SocialFollow.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Additional classes to be added to the root of the component. */
  className: string,
  /** An RSS feed path. */
  rssFeed: string,
  /** A Twitter handle. */
  twitterHandle: string,
  /** A Facebook profile name. */
  facebookProfile: string,
  /** A Pinterest profile name. */
  pinterestProfile: string,
  /** A YouTube channel name. */
  youtubeChannel: string,
  /** A Instagram profile name. */
  instagramProfile: string,
  /** A TikTok profile name. */
  tiktokProfile: string,
  coloured: bool
} : {};
export default SocialFollow;